import type { Query, QueryClient, QueryFilters, QueryKey } from "@tanstack/react-query";

import { trimCustomerListCacheAtKey } from "@/dashboard/features/hub/lib/customer-list-refresh-budget";
import { CustomerListWindowOwnership } from "@/dashboard/features/hub/realtime/customer-list-window-ownership";

export type CustomerListRefreshReason =
  | "event"
  | "explicit"
  | "mutation"
  | "online"
  | "resync"
  | "safety"
  | "unknown"
  | "visibility";

export type CustomerListRefetchType = "active" | "inactive" | "all";

export type CustomerListRefreshDiagnostics = {
  coalesced: number;
  completed: number;
  dirtyFollowups: number;
  failed: number;
  lastDurationMs: number | null;
  lastReasons: CustomerListRefreshReason[];
  requested: number;
  started: number;
};

type PendingRefresh = {
  reasons: Set<CustomerListRefreshReason>;
  shouldRefetch: boolean;
  trailing: boolean;
  trim: boolean;
};

type QueryRefreshState = {
  draining: Promise<void> | null;
  pending: PendingRefresh | null;
  query: Query;
};

type CoordinateCustomerListRefreshOptions = {
  predicate?: (queryKey: QueryKey) => boolean;
  queryKey: QueryKey;
  reason: CustomerListRefreshReason;
  refetchType?: CustomerListRefetchType;
  trim: boolean;
};

const coordinators = new WeakMap<QueryClient, CustomerListRefreshCoordinator>();

function emptyDiagnostics(): CustomerListRefreshDiagnostics {
  return {
    coalesced: 0,
    completed: 0,
    dirtyFollowups: 0,
    failed: 0,
    lastDurationMs: null,
    lastReasons: [],
    requested: 0,
    started: 0,
  };
}

function now(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

class CustomerListRefreshCoordinator {
  private readonly diagnostics = emptyDiagnostics();
  private readonly states = new Map<Query, QueryRefreshState>();

  constructor(private readonly queryClient: QueryClient) {}

  getDiagnostics(): CustomerListRefreshDiagnostics {
    return {
      ...this.diagnostics,
      lastReasons: [...this.diagnostics.lastReasons],
    };
  }

  coordinate(options: CoordinateCustomerListRefreshOptions): Promise<void> {
    this.diagnostics.requested++;
    const filters: QueryFilters = options.predicate
      ? { predicate: query => options.predicate?.(query.queryKey) ?? false }
      : { queryKey: options.queryKey };
    const queries = this.queryClient.getQueryCache().findAll(filters);

    // Mark every matching cache stale without letting React Query cancel or restart a fetch.
    const markStale = this.queryClient.invalidateQueries({ ...filters, refetchType: "none" });
    const work = queries.map(query => {
      const refetchType = options.refetchType ?? "active";
      const shouldRefetch =
        refetchType === "all" ||
        (refetchType === "active" && query.isActive()) ||
        (refetchType === "inactive" && !query.isActive());
      return this.enqueue({
        query,
        reason: options.reason,
        shouldRefetch,
        trim: options.trim,
      });
    });

    return Promise.all([markStale, ...work]).then(() => undefined);
  }

  private enqueue({
    query,
    reason,
    shouldRefetch,
    trim,
  }: {
    query: Query;
    reason: CustomerListRefreshReason;
    shouldRefetch: boolean;
    trim: boolean;
  }): Promise<void> {
    let state = this.states.get(query);
    if (!state) {
      state = { draining: null, pending: null, query };
      this.states.set(query, state);
    }

    const busy = !!state.draining || this.isFetching(query);
    if (busy) {
      this.diagnostics.coalesced++;
    }
    if (!state.pending) {
      state.pending = {
        reasons: new Set(),
        shouldRefetch: false,
        trailing: busy,
        trim: false,
      };
    }
    state.pending.reasons.add(reason);
    state.pending.shouldRefetch ||= shouldRefetch;
    state.pending.trailing ||= busy;
    state.pending.trim ||= trim;

    if (!state.draining) {
      state.draining = this.drain(state);
    }
    return state.draining;
  }

  private async drain(state: QueryRefreshState): Promise<void> {
    try {
      while (state.pending) {
        await this.waitUntilIdle(state.query);
        const pending = state.pending;
        state.pending = null;

        // Auth-scope cleanup can remove a query and create another with the same key.
        if (!pending.shouldRefetch || !this.isCurrentQuery(state.query)) {
          continue;
        }
        if (
          !CustomerListWindowOwnership.shouldRefresh(
            this.queryClient,
            state.query.queryKey,
            pending.reasons
          )
        ) {
          continue;
        }
        if (pending.trim) {
          trimCustomerListCacheAtKey(this.queryClient, state.query.queryKey);
        }

        if (pending.trailing) {
          this.diagnostics.dirtyFollowups++;
        }
        this.diagnostics.started++;
        this.diagnostics.lastReasons = [...pending.reasons].sort();
        const startedAt = now();
        try {
          await this.queryClient.invalidateQueries(
            {
              exact: true,
              queryKey: state.query.queryKey,
              predicate: query => query === state.query,
              // Eligibility was decided at enqueue time. Finish that refresh even if the
              // viewer navigated away while the previous read was in flight.
              refetchType: "all",
            },
            { cancelRefetch: false }
          );
          this.diagnostics.completed++;
        } catch {
          this.diagnostics.failed++;
        } finally {
          this.diagnostics.lastDurationMs = Math.max(0, now() - startedAt);
        }
      }
    } finally {
      state.draining = null;
      this.states.delete(state.query);
    }
  }

  private isCurrentQuery(query: Query): boolean {
    return this.queryClient.getQueryCache().get(query.queryHash) === query;
  }

  private isFetching(query: Query): boolean {
    return this.isCurrentQuery(query) && query.state.fetchStatus === "fetching";
  }

  private waitUntilIdle(query: Query): Promise<void> {
    if (!this.isFetching(query)) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      const unsubscribe = this.queryClient.getQueryCache().subscribe(() => {
        if (!this.isFetching(query)) {
          unsubscribe();
          resolve();
        }
      });
      if (!this.isFetching(query)) {
        unsubscribe();
        resolve();
      }
    });
  }
}

function getCoordinator(queryClient: QueryClient): CustomerListRefreshCoordinator {
  let coordinator = coordinators.get(queryClient);
  if (!coordinator) {
    coordinator = new CustomerListRefreshCoordinator(queryClient);
    coordinators.set(queryClient, coordinator);
  }
  return coordinator;
}

export function coordinateCustomerListRefresh(
  queryClient: QueryClient,
  options: CoordinateCustomerListRefreshOptions
): Promise<void> {
  return getCoordinator(queryClient).coordinate(options);
}

export function getCustomerListRefreshDiagnostics(
  queryClient: QueryClient
): CustomerListRefreshDiagnostics {
  return getCoordinator(queryClient).getDiagnostics();
}
